import { InputBase, ListItemText, MenuItem, SelectProps } from "@mui/material";
import { AutocompleteRenderGroupParams, AutocompleteRenderInputParams } from "@mui/material/Autocomplete/Autocomplete";
import { useSuspenseQueryHandler } from "@sinch/hooks/useSuspenseQueryHandler";
import { includes, isNil, pluck, propEq } from "ramda";
import React, { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { RateComponentsQuery, RateComponentsQueryVariables } from "../../../RateComponent/queries.types";
import { RateComponents } from "@sinch/components/application/RateComponent/queries.graphql";
import {
  GroupListSubheader,
  RateBaseAutocomplete,
  UnitLabelText,
} from "@sinch/components/application/RatelistBuilder/RateBuilderRowContent/RateInput/components/styled";

type RateBaseSelectProps = Omit<SelectProps<string>, "value" | "onChange" | "type"> & {
  onOpenChange: (open: boolean) => void;
  onChange: (val: string | null) => void;
  value: string | null;
  unit?: string | null;
  rateName?: string | null;
  availableRateComponents?: string[];
};

export const RateBaseSelect = ({
  onChange,
  onOpenChange,
  onFocus,
  value,
  onBlur,
  unit,
  rateName,
  readOnly,
  availableRateComponents,
}: RateBaseSelectProps) => {
  const { t } = useTranslation();
  const { data } = useSuspenseQueryHandler<RateComponentsQuery, RateComponentsQueryVariables>(RateComponents);
  const options: string[] = useMemo(
    () => [
      "",
      // archived components are not offered for new rates, but the current selection is kept readable
      ...pluck(
        "id",
        (data?.rateComponents || []).filter(({ archived, id }) => !archived || id === value)
      ).filter((id) => (isNil(availableRateComponents) || id === value ? true : includes(id, availableRateComponents))),
    ],
    [availableRateComponents, data?.rateComponents, value]
  );

  const getLabel = useCallback(
    (val: string) => data?.rateComponents.find(propEq("id", val))?.name ?? "",
    [data?.rateComponents]
  );

  const handleGetOptionLabel = useCallback(
    (val: string) => {
      if (val === "") {
        return rateName ?? "unknown";
      }
      return getLabel(val);
    },
    [getLabel, rateName]
  );

  const handleGroupBy = useCallback((option: string) => (option === "" ? "" : t("RateBuilder.percentageRate")), [t]);

  const handleAutocompleteChange = useCallback(
    (_: unknown, val: string) => {
      onChange(val);
    },
    [onChange]
  );

  const handleClose = useCallback(() => {
    onOpenChange(false);
  }, [onOpenChange]);

  const handleOpen = useCallback(() => {
    onOpenChange(true);
  }, [onOpenChange]);

  const handleRenderGroup = useCallback((params: AutocompleteRenderGroupParams) => {
    if (params.group === "") {
      return params.children;
    }
    return (
      <>
        <GroupListSubheader key={params.key}>{params.group}</GroupListSubheader>
        {params.children}
      </>
    );
  }, []);

  const handleRenderInput = useCallback(
    (params: AutocompleteRenderInputParams) => (
      <InputBase
        {...params.InputProps}
        fullWidth
        inputProps={{
          ...params.inputProps,
          "data-cy": "RateInput-valueInput-select",
        }}
        onBlur={(e) => onBlur?.(e as React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>)}
        onFocus={onFocus}
        readOnly={readOnly}
      />
    ),
    [onBlur, onFocus]
  );

  const handleRenderOption = useCallback(
    (props: React.HTMLAttributes<HTMLLIElement> & { key: any }, option: string) => (
      <MenuItem {...props} sx={{ display: "flex", gap: 2 }} value={option}>
        <ListItemText>{option === "" ? rateName : getLabel(option)}</ListItemText>
        <UnitLabelText variant="body2">{option === "" ? (unit ? `/${unit}` : "") : "%"}</UnitLabelText>
      </MenuItem>
    ),
    [rateName, getLabel, unit]
  );

  return (
    <RateBaseAutocomplete
      disableClearable
      fullWidth
      getOptionLabel={handleGetOptionLabel}
      groupBy={handleGroupBy}
      inputValue={handleGetOptionLabel(value ?? "")}
      onChange={handleAutocompleteChange}
      onClose={handleClose}
      onOpen={handleOpen}
      options={options}
      readOnly={readOnly}
      renderGroup={handleRenderGroup}
      renderInput={handleRenderInput}
      renderOption={handleRenderOption}
      slotProps={{
        popper: {
          "data-cy": "RateInput-valueInput-select-dropdown",
          style: { width: "fit-content" },
        },
      }}
      value={value ?? ""}
    />
  );
};
